import { Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React from 'react';

import type { PortfolioProjectRow, PortfolioSortField, SortDirection } from '@/api/portfolio/api';
import { appRoutes, isNewTabClick } from '@/app/routes';
import { HealthIndicator, MissingUpdateBadge } from '@/components/common/HealthIndicator';
import { PortfolioRowActions } from '@/components/pages/portfolio/PortfolioRowActions';

const SOW_TAG_LABEL: Record<string, string> = {
  active: 'Active',
  inactive: 'Inactive',
  draft: 'Draft',
  pending: 'Pending',
  expired: 'Expired',
  closed: 'Closed',
};

/** Tone modifier per SOW state: active = green, pending/draft = amber, else neutral. */
const SOW_TONE: Record<string, string> = {
  active: 'success',
  inactive: 'neutral',
  pending: 'warning',
  draft: 'warning',
  expired: 'neutral',
  closed: 'neutral',
};

/** Up-to-two-letter initials from a person's name for the avatar chip. */
const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

const antSortOrder = (
  columnField: PortfolioSortField,
  sortBy: PortfolioSortField | null,
  sortDir: SortDirection,
): 'ascend' | 'descend' | null => {
  if (sortBy !== columnField) {
    return null;
  }
  return sortDir === 'asc' ? 'ascend' : 'descend';
};

const formatTimeline = (start: string, end: string): string =>
  `${dayjs(start).format('MMM D, YYYY')} → ${dayjs(end).format('MMM D, YYYY')}`;

export interface BuildPortfolioColumnsParams {
  sortBy: PortfolioSortField | null;
  sortDir: SortDirection;
  onClientClick: (clientId: string) => void;
  onAddSharePointLink: (row: PortfolioProjectRow) => void;
}

/**
 * Builds the portfolio table columns. Sortable columns use `sorter: true`
 * (server-side sort) with a controlled `sortOrder` from the hook state.
 * Health metric columns are displayed for all users.
 */
export function buildPortfolioTableColumns({
  sortBy,
  sortDir,
  onClientClick,
  onAddSharePointLink,
}: BuildPortfolioColumnsParams): ColumnsType<PortfolioProjectRow> {
  return [
    {
      title: '',
      key: 'missing_update_flag',
      width: 44,
      align: 'center' as const,
      render: (_: unknown, row: PortfolioProjectRow) =>
        row.missing_update_flag ? <MissingUpdateBadge /> : null,
    },
    {
      title: 'Client',
      key: 'client_name',
      width: 140,
      ellipsis: true,
      fixed: 'left',
      sorter: true,
      sortOrder: antSortOrder('client_name', sortBy, sortDir),
      render: (_: unknown, row) => (
        <a
          href={appRoutes.projects(row.client_id)}
          className="pf-cell-client-link"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClientClick(row.client_id);
          }}
        >
          {row.client_name}
        </a>
      ),
    },
    {
      title: 'Project',
      key: 'project_name',
      width: 352,
      sorter: true,
      sortOrder: antSortOrder('project_name', sortBy, sortDir),
      render: (_: unknown, row) => (
        <div className="pf-cell-project">
          <a
            href={appRoutes.projectHome(row.client_id, row.id)}
            className="pf-cell-project__name"
            onClick={(e) => {
              // Modified clicks keep the browser's default (new tab); the row handler is skipped.
              if (isNewTabClick(e)) {
                e.stopPropagation();
                return;
              }
              // Plain clicks navigate in-app through the row handler, so cancel the full page load.
              e.preventDefault();
            }}
            onAuxClick={(e) => e.stopPropagation()}
          >
            {row.project_name}
          </a>
          <span className="pf-cell-project__id">{row.id}</span>
        </div>
      ),
    },
    {
      title: 'SOW',
      key: 'sow_status',
      align: 'center',
      width: 90,
      render: (_: unknown, row) =>
        row.sow_status ? (
          <Tag className={`pf-sow-tag pf-sow-tag--${SOW_TONE[row.sow_status] ?? 'neutral'}`}>
            {SOW_TAG_LABEL[row.sow_status] ?? row.sow_status}
          </Tag>
        ) : (
          <Tag className="pf-sow-tag pf-sow-tag--neutral">—</Tag>
        ),
    },
    {
      title: 'Status',
      key: 'overall_status',
      align: 'center' as const,
      width: 84,
      sorter: true,
      sortOrder: antSortOrder('overall_status', sortBy, sortDir),
      render: (_: unknown, row: PortfolioProjectRow) => (
        <HealthIndicator status={row.overall_status} context="Overall status" />
      ),
    },
    {
      title: 'On Sched.',
      key: 'on_schedule',
      align: 'center' as const,
      width: 96,
      render: (_: unknown, row: PortfolioProjectRow) => (
        <HealthIndicator status={row.on_schedule} context="On schedule" />
      ),
    },
    {
      title: 'On Budget',
      key: 'on_budget',
      align: 'center' as const,
      width: 96,
      render: (_: unknown, row: PortfolioProjectRow) => (
        <HealthIndicator status={row.on_budget} context="On Budget" />
      ),
    },
    {
      title: 'CSAT',
      key: 'csat',
      align: 'center' as const,
      width: 80,
      render: (_: unknown, row: PortfolioProjectRow) => (
        <HealthIndicator status={row.csat} context="CSAT" />
      ),
    },
    {
      title: 'Team Health',
      key: 'team_health',
      align: 'center' as const,
      width: 80,
      render: (_: unknown, row: PortfolioProjectRow) => (
        <HealthIndicator status={row.team_health} context="Team Health" />
      ),
    },
    { title: 'Offering', key: 'offering', dataIndex: 'offering', width: 140, ellipsis: true },
    { title: 'Owner', key: 'owner_name', dataIndex: 'owner_name', width: 120, ellipsis: true },
    {
      title: 'Leader',
      key: 'delivery_lead_name',
      width: 150,
      ellipsis: true,
      render: (_: unknown, row) => (
        <span className="pf-cell-lead">
          <span className="pf-avatar" aria-hidden="true">
            {initialsOf(row.delivery_lead_name)}
          </span>
          <span>{row.delivery_lead_name}</span>
        </span>
      ),
    },
    {
      title: 'PM',
      key: 'pm_name',
      width: 120,
      render: (_: unknown, row) => (
        <span className="pf-cell-pm">
          {row.pm_name}
          {row.pm_extra_count ? <span className="pf-pm-extra">+{row.pm_extra_count}</span> : null}
        </span>
      ),
    },
    {
      title: 'Timeline',
      key: 'start_date',
      width: 210,
      sorter: true,
      sortOrder: antSortOrder('start_date', sortBy, sortDir),
      render: (_: unknown, row) => (
        <span className="pf-cell-timeline">{formatTimeline(row.start_date, row.end_date)}</span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      fixed: 'right',
      align: 'center',
      render: (_: unknown, row) => (
        <PortfolioRowActions row={row} onAddSharePointLink={onAddSharePointLink} />
      ),
    },
  ];
}
